import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'deleted', 'all'
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [onlineFilter, setOnlineFilter] = useState('');
  const [suspendedFilter, setSuspendedFilter] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
  const [profileSetupFilter, setProfileSetupFilter] = useState('');
  const [appVersionFilter, setAppVersionFilter] = useState('');
  const [versionStats, setVersionStats] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, activeTab, dateFrom, dateTo, verifiedFilter, onlineFilter, suspendedFilter, deviceTypeFilter, profileSetupFilter, appVersionFilter]);

  useEffect(() => {
    const loadVersionStats = async () => {
      try {
        const res = await adminAPI.getAppVersionStats();
        setVersionStats(res.data);
      } catch {
        setVersionStats(null);
      }
    };
    loadVersionStats();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      
      // Search
      if (search) params.search = search;
      
      // Active/Deleted filter
      if (activeTab === 'deleted') {
        params.deleted = 'true';
      } else if (activeTab === 'all') {
        params.includeDeleted = 'true';
      }
      
      // Date filters
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      
      // Status filters
      if (verifiedFilter !== '') params.verified = verifiedFilter;
      if (onlineFilter !== '') params.online = onlineFilter;
      if (suspendedFilter !== '') params.suspended = suspendedFilter;
      if (profileSetupFilter !== '') params.profileSetup = profileSetupFilter;
      if (appVersionFilter.trim()) params.appVersion = appVersionFilter.trim();
      if (deviceTypeFilter) params.appPlatform = deviceTypeFilter;
      
      const response = await adminAPI.getAllUsers(params);
      // Ensure deviceType is set for each user (extract from fcmTokens if not present)
      const usersWithDeviceType = (response.data.users || []).map(user => {
        let deviceType = user.deviceType;
        
        // If deviceType is missing, try to get it from fcmTokens
        if (!deviceType || deviceType === 'unknown') {
          if (user.fcmTokens && user.fcmTokens.length > 0) {
            // Get the most recent token's platform
            const sortedTokens = [...user.fcmTokens].sort((a, b) => {
              const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
              const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
              return dateB - dateA;
            });
            deviceType = sortedTokens[0]?.platform || 'unknown';
          }
          // Fallback to subscription platform
          if ((!deviceType || deviceType === 'unknown') && user.subscription?.platform) {
            deviceType = user.subscription.platform;
          }
        }
        
        return {
          ...user,
          deviceType: deviceType || 'unknown'
        };
      });
      
      setUsers(usersWithDeviceType);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await adminAPI.deleteUser(userId);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleRestore = async (userId) => {
    if (!window.confirm('Are you sure you want to restore this user?')) return;

    try {
      await adminAPI.restoreUser(userId);
      alert('User restored successfully!');
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to restore user');
    }
  };

  const handleSuspend = async (userId, user) => {
    const isSuspended = user?.suspended && !user?.isBanned;
    const next = !isSuspended;
    if (user?.isBanned) {
      alert('User is permanently banned. Use Unban to restore access.');
      return;
    }
    const confirmText = next
      ? 'Suspend this user temporarily? They will be locked out but profile data is kept. Device bans lift on unsuspend.'
      : 'Unsuspend this user? Their devices will be unbanned.';
    if (!window.confirm(confirmText)) return;
    try {
      if (next) {
        const reasonCode =
          prompt(
            'Client-facing reason: TERMS_VIOLATION, SAFETY, or UNDER_REVIEW',
            'UNDER_REVIEW',
          ) || 'UNDER_REVIEW';
        await adminAPI.updateUser(userId, { suspended: true, reasonCode });
      } else {
        await adminAPI.updateUser(userId, { suspended: false });
      }
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update suspension status');
    }
  };

  const handleBan = async (userId, user) => {
    const isBanned = Boolean(user?.isBanned);
    const next = !isBanned;
    const confirmText = next
      ? 'Permanently ban this user? This scrubs their public profile data and community posts. Device bans stay until unban.'
      : 'Unban this user? Access is restored but scrubbed profile data is NOT recovered. Devices will be unbanned.';
    if (!window.confirm(confirmText)) return;

    try {
      if (next) {
        const reasonCode =
          prompt(
            'Client-facing reason: TERMS_VIOLATION, SAFETY, or UNDER_REVIEW',
            'SAFETY',
          ) || 'SAFETY';
        const internalNote = prompt('Internal note (optional, never shown to user):') || undefined;
        await adminAPI.banUser(userId, { banned: true, reasonCode, reason: internalNote });
      } else {
        await adminAPI.banUser(userId, { banned: false });
      }
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to ban/unban user');
    }
  };

  const handleVerify = async (userId, current) => {
    const next = !current;
    const confirmText = next ? 'Verify this user?' : 'Unverify this user?';
    if (!window.confirm(confirmText)) return;
    try {
      await adminAPI.updateUser(userId, { isVerified: next });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update verification status');
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setVerifiedFilter('');
    setOnlineFilter('');
    setSuspendedFilter('');
    setDeviceTypeFilter('');
    setProfileSetupFilter('');
    setSearch('');
    setPage(1);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Users</h1>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search name, email, username..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
          />
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => {
            setActiveTab('active');
            setPage(1);
          }}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'active'
              ? 'border-b-2 border-pink-500 text-pink-500'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Active Users
        </button>
        <button
          onClick={() => {
            setActiveTab('deleted');
            setPage(1);
          }}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'deleted'
              ? 'border-b-2 border-pink-500 text-pink-500'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Soft-deleted (admin)
        </button>
        <button
          onClick={() => {
            setActiveTab('all');
            setPage(1);
          }}
          className={`px-4 py-2 font-semibold transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-pink-500 text-pink-500'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Users
        </button>
      </div>

      {versionStats?.breakdown?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">App versions (active users)</h2>
          <div className="flex flex-wrap gap-2">
            {versionStats.breakdown.slice(0, 12).map((row) => (
              <button
                key={`${row.platform}-${row.version}-${row.build}`}
                type="button"
                onClick={() => {
                  setAppVersionFilter(row.version);
                  setPage(1);
                }}
                className="px-3 py-1.5 rounded-full text-xs font-semibold bg-pink-50 text-pink-800 border border-pink-200 hover:bg-pink-100"
              >
                {(row.platform || 'unknown').toUpperCase()} {row.version}
                {row.build != null ? ` (${row.build})` : ''}: {row.count}
              </button>
            ))}
            {versionStats.unknownCount > 0 && (
              <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                Unknown version: {versionStats.unknownCount}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-8 gap-4">
          {/* Date From */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            />
          </div>
          
          {/* Date To */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            />
          </div>
          
          {/* Verified Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Verified</label>
            <select
              value={verifiedFilter}
              onChange={(e) => {
                setVerifiedFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            >
              <option value="">All</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
          </div>
          
          {/* Online Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Online</label>
            <select
              value={onlineFilter}
              onChange={(e) => {
                setOnlineFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            >
              <option value="">All</option>
              <option value="true">Online</option>
              <option value="false">Offline</option>
            </select>
          </div>
          
          {/* Suspended Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Suspended</label>
            <select
              value={suspendedFilter}
              onChange={(e) => {
                setSuspendedFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            >
              <option value="">All</option>
              <option value="true">Suspended</option>
              <option value="false">Not Suspended</option>
            </select>
          </div>
          
          {/* Device Type Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Device Type</label>
            <select
              value={deviceTypeFilter}
              onChange={(e) => {
                setDeviceTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            >
              <option value="">All</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
              <option value="web">Web</option>
              <option value="unknown">Unknown</option>
            </select>
          </div>
          
          {/* App Version Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">App Version</label>
            <input
              type="text"
              placeholder="e.g. 7.2.0"
              value={appVersionFilter}
              onChange={(e) => {
                setAppVersionFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            />
          </div>

          {/* Profile Setup Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Profile Setup</label>
            <select
              value={profileSetupFilter}
              onChange={(e) => {
                setProfileSetupFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none"
            >
              <option value="">All</option>
              <option value="true">Complete</option>
              <option value="false">Incomplete</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">SR No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Device Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">App Version</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Profile Setup</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Signup Time</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Last Seen</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user, index) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-600 font-semibold">{(page - 1) * 20 + index + 1}</td>
                    <td className="px-6 py-4 text-gray-600">
                      <div>{user.email}</div>
                      {user.isBanned && (
                        <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Banned</span>
                      )}
                      {user.suspended && !user.isBanned && (
                        <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">Suspended</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.deviceType === 'ios'
                          ? 'bg-blue-100 text-blue-700'
                          : user.deviceType === 'android'
                          ? 'bg-green-100 text-green-700'
                          : user.deviceType === 'web'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {user.deviceType ? user.deviceType.toUpperCase() : 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600 text-sm">
                      <div>{user.appVersionLabel || 'Unknown'}</div>
                      {user.appVersionSeenAt && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          seen {new Date(user.appVersionSeenAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        user.profileSetup
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {user.profileSetup ? 'Complete' : 'Incomplete'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {user.lastSeen ? new Date(user.lastSeen).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link
                          to={`/users/${user._id}`}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-200 transition-colors"
                        >
                          View
                        </Link>
                        {!user.isDeleted && (
                          <>
                            <button
                              onClick={() => handleVerify(user._id, user.isVerified)}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                                user.isVerified
                                  ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {user.isVerified ? 'Unverify' : 'Verify'}
                            </button>
                            <button
                              onClick={() => handleSuspend(user._id, user)}
                              disabled={user.isBanned}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                                user.suspended && !user.isBanned
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : user.isBanned
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                            >
                              {user.suspended && !user.isBanned ? 'Unsuspend' : 'Suspend'}
                            </button>
                            <button
                              onClick={() => handleBan(user._id, user)}
                              className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors ${
                                user.isBanned
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              }`}
                            >
                              {user.isBanned ? 'Unban' : 'Ban'}
                            </button>
                            <button
                              onClick={() => handleDelete(user._id)}
                              className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-semibold hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </>
                        )}
                        {user.isDeleted && (
                          <button
                            onClick={() => handleRestore(user._id)}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-semibold hover:bg-green-200 transition-colors"
                          >
                            Restore
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Previous
            </button>
            <span className="text-gray-700 font-semibold">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:border-pink-500 transition-colors"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Users;
